import React, { useState, useEffect } from 'react';
import './SalarySummary.css';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';
import { CabinetModal } from '../components/activity/CabinetModal';
import { EmployeeInfoModal } from '../components/activity/EmployeeInfoModal';
import { SalaryCell } from '../components/activity/SalaryCell';
import { useSchoolHours } from './useSchoolHours';
import { useCabinetCache, useEmployeesCache, usePayrollCache } from '../hooks/useEmployeeCache';

/**
 * Composant SalaryRow - Une ligne du tableau avec vérification du montant
 */
function SalaryRow({ operator, onOpenEmployeeModal, onNavigate, currentMonth }) {
  const { loading, exists: hasSalary } = usePayrollCache(operator.id, currentMonth);

  // Vérifier si le mois sélectionné est terminé
  const today = new Date();
  const isMonthComplete = currentMonth.getFullYear() < today.getFullYear() || 
                         (currentMonth.getFullYear() === today.getFullYear() && 
                          currentMonth.getMonth() < today.getMonth());

  const handleGeneratePayslip = () => {
    // Naviguer vers la page de génération de fiche de paie
    onNavigate('payroll', { 
      operatorId: operator.id, // Utiliser l'ID numérique, pas l'objet complet
      year: currentMonth.getFullYear(),
      month: currentMonth.getMonth() + 1,
      monthlyHours: operator.monthlyHours // Passer les heures réelles du tableau
    });
  };

  const handleViewPayslip = () => {
    // Naviguer vers la page de visualisation de la fiche de paie
    onNavigate('payroll', { 
      operatorId: operator.id,
      year: currentMonth.getFullYear(),
      month: currentMonth.getMonth() + 1,
      viewMode: true // Mode visualisation
    });
  };

  return (
    <tr className="table-row">
      <td className="salary-cell">
        <SalaryCell
          operatorId={operator.id}
          currentMonth={currentMonth}
        />
      </td>
      <td className="status-cell">
        {operator.status === 'Informations requises' ? (
          <button
            className="status-badge status-informations-requises clickable"
            onClick={() => onOpenEmployeeModal(operator.operator || operator.id)}
            title="Cliquer pour compléter les informations"
          >
            {operator.status}
          </button>
        ) : hasSalary ? (
          <button
            className="view-payslip-button"
            onClick={handleViewPayslip}
            title="Voir la fiche de paie"
          >
            Voir la fiche de paie
          </button>
        ) : isMonthComplete ? (
          <button
            className="generate-payslip-button"
            onClick={handleGeneratePayslip}
            title="Générer la fiche de paie"
          >
            Générer la fiche de paie
          </button>
        ) : (
          <span className="status-badge status-en-cours">
            En cours
          </span>
        )}
      </td>
    </tr>
  );
}

/**
 * Composant SalarySummary - Section droite du tableau mensuel
 * Affiche le montant du salaire et le statut d'envoi pour chaque opérateur
 * Ou un empty state si aucun cabinet n'existe
 * Le calcul du salaire inclut les heures de formation pour les apprentis
 */
export function SalarySummary({ operators = [], series = {}, rawData = [], currentMonth, onMonthChange, onNavigate }) {
  // Récupérer les données école pour inclure les heures de formation dans le calcul
  const { schoolData } = useSchoolHours(currentMonth);
  
  // 🔥 Utiliser les hooks cachés (pas de rechargement répété)
  const { cabinet, loading: cabinetLoading, error: cabinetError, refresh: refreshCabinet } = useCabinetCache();
  const operatorIds = operators.map(op => op.id);
  const { employees: employeeData, loading: employeeLoading, refresh: refreshEmployees } = useEmployeesCache(operatorIds);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);

  // Navigation vers la page de configuration du cabinet
  const handleCompleteCabinet = () => {
    setModalOpen(true);
  };

  // Ouvrir le modal employé
  const handleOpenEmployeeModal = (operator) => {
    if (!cabinet) {
      alert('Veuillez d\'abord créer un cabinet avant d\'ajouter des employés.');
      return;
    }
    setSelectedOperator(operator);
    setEmployeeModalOpen(true);
  };

  // Fermer le modal employé
  const handleCloseEmployeeModal = () => {
    setEmployeeModalOpen(false);
    setSelectedOperator(null);
  };

  // Callback après sauvegarde d'un employé
  const handleEmployeeSaved = () => {
    // Rafraîchir silencieusement les données des employés
    refreshEmployees();
  };

  // Callback après sauvegarde du cabinet
  const handleCabinetSaved = () => {
    // Rafraîchir silencieusement les données du cabinet et des employés
    refreshCabinet();
    refreshEmployees();
  };

  // Calculer les heures mensuelles d'un opérateur pour le mois sélectionné
  const calculateMonthlyHours = (operatorId) => {
    if (!Array.isArray(rawData) || rawData.length === 0) return 0;
    
    // Obtenir l'année et le mois du mois sélectionné
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Filtrer les données pour ce mois et cet opérateur
    const monthData = rawData.filter(row => {
      if (row.operator_id !== operatorId) return false;
      
      const rowDate = new Date(row.local_date);
      return rowDate.getFullYear() === year && rowDate.getMonth() === month;
    });
    
    // Calculer le total des heures (work_minutes converti en heures)
    const totalMinutes = monthData.reduce((sum, row) => {
      return sum + (row.work_minutes || 0);
    }, 0);
    
    const totalHours = totalMinutes / 60;
    return Math.round(totalHours);
  };

  // Générer les données réelles à partir des opérateurs
  const realData = Array.isArray(operators) ? operators.map(operator => {
    const empData = employeeData[operator.id];
    const hasEmployeeData = empData && empData.hasData;
    const employeeId = empData?.employeeId || null;
    const monthlyHours = calculateMonthlyHours(operator.id);
    
    // Vérifier si le mois sélectionné est terminé
    const today = new Date();
    const isMonthComplete = currentMonth.getFullYear() < today.getFullYear() || 
                           (currentMonth.getFullYear() === today.getFullYear() && 
                            currentMonth.getMonth() < today.getMonth());
    
    // Ajouter les heures de formation UNIQUEMENT si le mois est terminé
    const hasSchool = schoolData[operator.id];
    const trainingHours = (hasSchool && isMonthComplete) ? hasSchool.accumulatedHours : 0;
    const totalHoursForSalary = monthlyHours + trainingHours;
    
    console.log(`💰 Salary Data - ${operator.name}: employeeId=${employeeId}, monthlyHours=${monthlyHours}, trainingHours=${trainingHours}, isMonthComplete=${isMonthComplete}, totalForSalary=${totalHoursForSalary}, hasData=${hasEmployeeData}`);
    
    return {
      id: operator.id,
      name: operator.name,
      salary: 'NR', // Sera remplacé par SalaryCell
      status: hasEmployeeData ? 'Envoyé' : 'Informations requises', // statut basé sur les données employees
      operator: operator, // Ajouter l'objet opérateur complet
      hasEmployeeData: hasEmployeeData,
      employeeId: employeeId, // ID de l'employé pour le calcul de salaire
      monthlyHours: totalHoursForSalary // Heures pour calcul salaire (avec formation si mois terminé)
    };
  }) : [];

  // Fonction pour obtenir la couleur du statut d'envoi
  const getStatusColor = (status) => {
    switch (status) {
      case 'Envoyé': return '#4CAF50'; // Vert
      case 'Informations requises': return '#FF9800'; // Orange
      case 'en cours d\'envoi': return '#FF9800'; // Orange
      case 'pas envoyé': return '#F44336'; // Rouge
      default: return '#666';
    }
  };

  // Formatage du mois (réutilisé pour cohérence)
  const formatMonth = (date) => {
    return new Intl.DateTimeFormat('fr-FR', { 
      year: 'numeric', 
      month: 'long' 
    }).format(date);
  };

  // Formatage du montant en euros
  const formatSalary = (amount) => {
    if (amount === 'NR') return 'NR';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Fonction pour rendre le contenu selon l'état
  const renderContent = () => {
    // Si pas de cabinet, afficher l'empty state
    if (!cabinetLoading && !cabinetError && !cabinet) {
      return (
        <div className="salary-summary">
          <div className="cabinet-empty-state">
            <div className="cabinet-icon">🏢</div>
            <h3 className="cabinet-empty-title">
              Complétez les informations pour générer des fiches de paies
            </h3>
            <p className="cabinet-empty-description">
              Renseignez les informations de l'employeur et des salariés afin d'activer la génération automatique des fiches de paie.
            </p>
            <button 
              className="cabinet-complete-button"
              onClick={handleCompleteCabinet}
            >
              Compléter mes informations
            </button>
          </div>
        </div>
      );
    }

    // Si erreur de chargement du cabinet
    if (cabinetError) {
      return (
        <div className="salary-summary">
          <div className="cabinet-error-state">
            <div className="cabinet-error-title">Erreur</div>
            <div className="cabinet-error-message">
              Impossible de charger les informations du cabinet.
            </div>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              Réessayer
            </button>
          </div>
        </div>
      );
    }

    // Si chargement du cabinet
    if (cabinetLoading) {
      return (
        <div className="salary-summary">
          <div className="cabinet-loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Chargement...</div>
          </div>
        </div>
      );
    }

    // Pas d'état de chargement pour employees - afficher immédiatement avec cache

    // État normal avec cabinet
    return (
      <div className="salary-summary">
        {/* En-tête avec navigation par mois */}
        <div className="summary-header">
          <div className="month-navigation">
            <button 
              className="month-arrow" 
              onClick={() => onMonthChange && onMonthChange('prev')}
              title="Mois précédent"
            >
              ←
            </button>
            <span className="current-month">{formatMonth(currentMonth)}</span>
            <button 
              className="month-arrow" 
              onClick={() => onMonthChange && onMonthChange('next')}
              title="Mois suivant"
            >
              →
            </button>
          </div>
        </div>

        {/* Vrai tableau comme AttendanceTable */}
        <div className="salary-table-wrapper">
          <table className="salary-table">
            <thead>
              <tr>
                <th>Montant salaire</th>
                <th>Statut envoi</th>
              </tr>
            </thead>
            <tbody>
              {realData.map((operator) => (
                <SalaryRow
                  key={operator.id}
                  operator={operator}
                  onOpenEmployeeModal={handleOpenEmployeeModal}
                  onNavigate={onNavigate}
                  currentMonth={currentMonth}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Rendu principal */}
      {renderContent()}
      
      {/* Modal pour les informations du cabinet */}
      <CabinetModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        existing={cabinet}
        onSaved={handleCabinetSaved}
      />

      {/* Modal pour les informations employé */}
      <EmployeeInfoModal
        isOpen={employeeModalOpen}
        onClose={handleCloseEmployeeModal}
        employee={null} // Mode création
        operatorId={selectedOperator?.id}
        cabinet={cabinet}
        onSaved={handleEmployeeSaved}
      />
    </>
  );
}